import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiClient } from '../client';
import { AccessNotProvisionedError, ApiError, isRetryableApiError } from '../errors';
import { DASHBOARD_ROLES, type Session } from '@/types/session';

export const ME_QUERY_KEY = ['session', 'me'] as const;

/**
 * The wire contract for GET /api/v1/me is the backend's, not this union's —
 * `role` in particular must be checked against DASHBOARD_ROLES rather than
 * trusted, or a role the frontend doesn't recognise yet (a rename, a rollout
 * lag, a misconfigured environment) flows through as a valid DashboardRole and
 * crashes the first unguarded ROLE_ROUTES[role] lookup instead of failing here.
 */
const sessionSchema = z.object({
  displayName: z.string(),
  email: z.string(),
  role: z.enum(DASHBOARD_ROLES),
  organisationName: z.string().nullable(),
  permissions: z.array(z.string()),
}) satisfies z.ZodType<Session>;

/**
 * Fetched once after sign-in and treated as the authoritative session. Long
 * staleTime because role changes arrive via the backend's own cache TTL, not by
 * polling, and invalidated on sign-out.
 */
export function meQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => sessionSchema.parse(await api.request<unknown>('/api/v1/me')),
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      // The regression test for the infinite loop lives on this line. An
      // unprovisioned 401 is terminal: re-requesting /me cannot change the
      // answer, and retrying it is how the loop starts.
      if (error instanceof AccessNotProvisionedError) return false;
      if (error instanceof ApiError && !isRetryableApiError(error)) return false;
      return failureCount < 2;
    },
  });
}
